/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";

/**
 * FeedbackPrompt, dismissible floating card used by SpaceDetail (dwell view)
 * and Region (post-external-click return). Renders a 1-5 star scale and an
 * optional comment box. Silently no-ops if the user has already given
 * feedback for the same (slug, context) in this browser.
 *
 * Props:
 *  - workspaceSlug  (required)
 *  - workspaceName  (display only)
 *  - context        'view' | 'external_return' | 'booking_prompt'
 *  - visible        controlled by parent (true/false)
 *  - onDismiss      callback when the user dismisses
 *  - askBooked      show "Did you book?" yes/no toggle (context=external_return)
 */
export default function FeedbackPrompt({
  workspaceSlug,
  workspaceName,
  context = "view",
  visible = true,
  onDismiss,
  askBooked = false,
}) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [booked, setBooked] = useState(null);   // null | true | false
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // Local suppression key so the prompt doesn't nag the same user twice.
  const dismissKey = `metier_fb_dismissed_${context}_${workspaceSlug}`;

  useEffect(() => {
    if (!visible) return;
    try {
      if (sessionStorage.getItem(dismissKey) === "1") {
        onDismiss && onDismiss();
      }
    } catch { /* ignore */ }
  }, [visible, dismissKey]); // eslint-disable-line

  if (!visible || !workspaceSlug) return null;

  const closeSilently = () => {
    try { sessionStorage.setItem(dismissKey, "1"); } catch {}
    onDismiss && onDismiss();
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/feedback/quick", {
        workspace_slug: workspaceSlug,
        context,
        rating: rating || null,
        booked: askBooked ? booked : null,
        comment: comment.trim() || null,
      });
      setSent(true);
      try { sessionStorage.setItem(dismissKey, "1"); } catch {}
      setTimeout(() => { onDismiss && onDismiss(); }, 1400);
    } catch { /* silent fail */ }
    setBusy(false);
  };

  const heading = context === "external_return"
    ? <>How did <em>{workspaceName || "that space"}</em> go?</>
    : <>How does <em>{workspaceName || "this space"}</em> look?</>;

  const subCopy = context === "external_return"
    ? "You just came back from the partner site. A quick note helps us learn what's working."
    : "You've been looking at this space for a bit. A quick reaction helps us calibrate the network.";

  return (
    <div className="m-fb-prompt" role="dialog" aria-label="Quick feedback" data-testid={`feedback-prompt-${context}`}>
      <button
        type="button"
        className="m-fb-close"
        onClick={closeSilently}
        aria-label="Dismiss"
        data-testid={`feedback-close-${context}`}
      >×</button>

      {sent ? (
        <div className="m-fb-thanks" data-testid={`feedback-thanks-${context}`}>
          <span className="m-eyebrow" style={{ color: "var(--gold)" }}>Received</span>
          <div className="m-fb-thanks-h">Thanks, noted.</div>
        </div>
      ) : (
        <>
          <span className="m-eyebrow" style={{ color: "var(--gold)" }}>Quick feedback</span>
          <div className="m-fb-h">{heading}</div>
          <p className="m-fb-sub">{subCopy}</p>

          {askBooked && (
            <div className="m-fb-yesno" data-testid={`feedback-yesno-${context}`}>
              <button
                type="button"
                className={`m-fb-yesno-btn ${booked === true ? "on" : ""}`}
                onClick={() => setBooked(true)}
                data-testid="feedback-booked-yes"
              >I booked</button>
              <button
                type="button"
                className={`m-fb-yesno-btn ${booked === false ? "on" : ""}`}
                onClick={() => setBooked(false)}
                data-testid="feedback-booked-no"
              >Just browsed</button>
            </div>
          )}

          <div className="m-fb-stars" role="radiogroup" aria-label="Rating" data-testid={`feedback-stars-${context}`}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                className={`m-fb-star ${rating >= n ? "on" : ""}`}
                aria-label={`${n} star${n>1?"s":""}`}
                data-testid={`feedback-star-${n}-${context}`}
              >★</button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 280))}
            placeholder="Optional, what stood out?"
            rows={2}
            className="m-fb-comment"
            data-testid={`feedback-comment-${context}`}
          />

          <div className="m-fb-actions">
            <button
              type="button"
              onClick={closeSilently}
              className="m-btn-ghost m-fb-skip"
              data-testid={`feedback-skip-${context}`}
            >Not now</button>
            <button
              type="button"
              onClick={submit}
              className="m-btn-gold m-fb-submit"
              disabled={busy || (rating === 0 && !comment.trim() && booked === null)}
              data-testid={`feedback-submit-${context}`}
            >{busy ? "Sending…" : "Send →"}</button>
          </div>
        </>
      )}
    </div>
  );
}
